/* eslint-disable react/prop-types */
import NextApp from 'next/app';
import getConfig from 'next/config';
import React, { useEffect } from 'react';
import Helmet from 'react-helmet';
import useSWR, { cache } from 'swr';
import { AnimateSharedLayout } from 'framer-motion';
import MetaTags from '@glowforge/ui-common/components/contentful-views/util/meta-tags';
import PageMetadata from '@glowforge/ui-common/components/contentful-views/page-metadata';
import { interpretMetadata } from '@glowforge/ui-common/models/page-metadata';
import { lazyLoadForceCheck, CookieNotificationWidget } from '@glowforge/ui-common/components';
import Analytics from '../components/analytics';
import Navbar from '../components/navbar';
import Footer from '../components/footer';
import RouterProgressBar from '../components/router-progress-bar/router-progress-bar';
import { UserProvider } from '../context/UserContext';
import CountryCodeContext from '../context/CountryCodeContext';
import FeatureFlagsContext from '../context/FeatureFlagsContext';
import { ReferralInfoProvider } from '../context/ReferralInfoContext';
import ModalPriorityProvider from '../context/ModalPriorityContext/ModalPriorityProvider';
import { getGlobalFooter, getDefaultMetadata, getCookieNotification } from '../api/contentful';
import { getAllFlagsState, allFlagsStateVariable } from '../api/feature-flags';
import { CountryCodes } from '../constants';
import favicon from '../favicon.png';
import clientOnly from '../util/client-only';

import '../polyfills';
import './app.less';

const {
  publicRuntimeConfig: {
    GF_DOTCOM_MADLIB_ENDPOINT,
  },
} = getConfig();

const REACT_ROOT = '#__next';
const APP_CACHE_KEY = 'app';

const getCountryCode = (appContext) => (
  clientOnly(() => window.GF_COUNTRY_CODE)
    || appContext.ctx.req.headers['cloudfront-viewer-country']
    || CountryCodes.US
);

const getMetadata = () => {
  const tags = new MetaTags();

  tags.push({ 'char-set': 'utf-8' });
  tags.push({ 'http-equiv': 'x-ua-compatible', content: 'ie=edge' });
  tags.push('viewport', 'width=device-width');
  tags.name('robots', 'noodp');
  tags.openGraph('locale', 'en_US');

  return tags.tags;
};

const getLinks = () => {
  const links = [{
    rel: 'alternate',
    type: 'application/rss+xml',
    title: 'Glowforge - the 3D Laser Printer » Home Comments Feed',
    href: 'https://blog.glowforge.com/feed/',
  }, {
    rel: 'shortcut icon',
    href: favicon,
  }];

  return links;
};

/**
 * Fetch data required by all pages
 */
const fetchData = async () => {
  const [
    defaultMetadata,
    footerContent,
    cookieNotification,
    allFlags,
  ] = await Promise.all([
    getDefaultMetadata(),
    getGlobalFooter(),
    getCookieNotification(),
    getAllFlagsState(),
  ]);

  return {
    defaultMetadata,
    footerContent,
    cookieNotification,
    allFlags,
  };
};

const App = ({
  initialData,
  countryCode,
  pageProps,
  Component,
}) => {
  const appData = useSWR(
    APP_CACHE_KEY,
    () => fetchData(),
    { initialData, revalidateOnMount: true },
  );
  const {
    allFlags,
    defaultMetadata,
    footerContent,
    cookieNotification,
  } = appData.data;

  // `navbarProps` is a temporary workaround for STORE-4341. Eventually we
  // will need a way to compose layouts for different pages that doesn't rely
  // on this indirect approach
  const {
    navbarProps = {},
    includeFooter = true,
  } = pageProps;
  const interpretedMetadata = interpretMetadata(defaultMetadata);
  const { pageTitle: defaultTitle, ...trimmedMetadata } = interpretedMetadata;

  useEffect(() => {
    // Avoid a bug caused by a race between preloaded CSS and JavaScript assets.
    // If JS executes before CSS is available lazy loaded image positions will
    // be different than their final position and we need to recheck.
    setTimeout(() => lazyLoadForceCheck());

    // If for some reason CSS was not applied on the next tick, check again when
    // window is loaded
    window.addEventListener('load', () => lazyLoadForceCheck());
  }, []);

  /* eslint-disable react/jsx-props-no-spreading */
  return (
    <ModalPriorityProvider>
      <CountryCodeContext.Provider value={countryCode}>
        <FeatureFlagsContext.Provider value={allFlags}>
          <UserProvider>
            <ReferralInfoProvider>
              <div className='app'>
                <Analytics />
                <Helmet
                  titleTemplate='%s | Glowforge'
                  meta={getMetadata()}
                  link={getLinks()}>
                  <script type='text/javascript'>
                    {`
                      window._gf_react_root = '${REACT_ROOT}';
                      window.GF_MADLIB_ENDPOINT='${GF_DOTCOM_MADLIB_ENDPOINT}';
                      window.GF_COUNTRY_CODE='${countryCode}';
                      window.${allFlagsStateVariable}=${JSON.stringify(allFlags)};
                    `}
                  </script>
                </Helmet>
                <PageMetadata
                  {...trimmedMetadata}
                  pageTitle={defaultTitle || 'Glowforge'} />
                <Navbar {...navbarProps} />
                <RouterProgressBar />
                <AnimateSharedLayout>
                  <Component {...pageProps} />
                </AnimateSharedLayout>
                {(includeFooter && footerContent) ? (
                  <Footer footerContent={footerContent} />
                ) : null}
              </div>
              {cookieNotification ? (
                <CookieNotificationWidget model={cookieNotification} />
              ) : null}
            </ReferralInfoProvider>
          </UserProvider>
        </FeatureFlagsContext.Provider>
      </CountryCodeContext.Provider>
    </ModalPriorityProvider>
  );
};

App.getInitialProps = async (appContext) => {
  const countryCode = getCountryCode(appContext);
  const [
    initialData,
    appProps,
  ] = await Promise.all([
    cache.get(APP_CACHE_KEY) || fetchData(),
    NextApp.getInitialProps(appContext),
  ]);

  return {
    initialData,
    countryCode,
    ...appProps,
  };
};

export default App;
